import type {
  ScaleLinear,
  ScaleLogarithmic,
  ScaleSymLog,
  ScaleTime,
} from 'd3-scale';

import type { ChartUnit } from '../../../types/chart-unit.js';
import type { Formatter } from '../../../types/formatter.js';
import type { TickValueFormatter } from '../../../types/tick-value.js';
import { isLogScale, isSymlogScale } from '../../../utils/detect-scale.js';
import {
  tickValueFormatter,
  formatValue,
} from '../../../utils/format-value.js';

const sanitizeEpsilonValue = (value: number) =>
  value === Number.EPSILON ? 0 : value;

/**
 * Gets y-axis tick formatter depending on the type of scale.
 *
 * As the log scale doesn't support zero or negative values,
 * the smallest number is EPSILON, and it must be sanitized into a 0
 * to represent better human-readable ticks.
 *
 * @param scale - axis scale
 * @param unit - axis unit
 * @param formatter - axis formatter
 * @param numTicks - proposed number of ticks
 * @param precision - tick values precision
 *
 * @returns formatter
 */
export function getYAxisTickFormatter(
  scale:
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>
    | ScaleLinear<number, number>
    | ScaleTime<number, number>,
  unit?: ChartUnit,
  formatter?: Formatter,
  numTicks?: number,
  precision?: number,
): TickValueFormatter {
  const valueFormatter = formatter ?? formatValue;
  const logFormatter =
    (scale: ScaleLogarithmic<number, number> | ScaleSymLog<number, number>) =>
    (value: number) => {
      // ScaleSymLog inherits linearish().tickFormat which never returns ''.
      // Use a power-of-10 check (matching what ScaleLogarithmic.tickFormat does internally)
      // to detect major ticks for symlog scales.
      const isSymlogMajorTick = (v: number) => {
        const abs = Math.abs(v);
        if (abs === 0) {
          return true;
        }
        const log = Math.log10(abs);
        return Math.abs(log - Math.round(log)) < 1e-9;
      };
      const isMajorValue = isSymlogScale(scale)
        ? isSymlogMajorTick(value)
        : scale.tickFormat(numTicks)(value) !== '';
      const nonEpsilonValue = sanitizeEpsilonValue(value);

      return isMajorValue
        ? valueFormatter(nonEpsilonValue, unit, precision)
        : '';
    };
  const linearFormatter = (value: number) =>
    valueFormatter(value, unit, precision);

  const yAxisTickFormatter =
    isLogScale(scale) || isSymlogScale(scale)
      ? logFormatter(scale)
      : linearFormatter;

  return tickValueFormatter(yAxisTickFormatter, unit);
}
